import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
} from "axios";

import { errors, TRANSLATION_KEY } from "../../constants";
import { ResponseStatusEnum } from "../../enums";
import {
  ApiException,
  IApiError,
  ITranslatableItem,
  RequestBodyModel,
  TApiSuccess,
} from "../../models";
import { Log } from "../log.helpers";
import {
  EMPTY_RESPONSE_ERROR,
  logApiError,
  normalizeApiError,
} from "./api-utils";
import { handleForbiddenResponseException } from "./api-utils.exception-handler.forbidden";
import { handleUnauthorizedResponseException } from "./api-utils.exception-handler.unauthorized";

export interface IHandleApiException {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  e: any;
  axiosConfig: AxiosRequestConfig<RequestBodyModel>;
}

export const handleApiException = async <T>({
  e,
  axiosConfig,
}: IHandleApiException): Promise<
  AxiosResponse<TApiSuccess<T> | IApiError> | undefined
> => {
  if (axios.isCancel(e)) {
    throw e instanceof Error ? e : new Error(String(e));
  }

  const failedResponse = (e as AxiosError<IApiError, unknown>).response;

  if (!failedResponse?.status) {
    throw new ApiException(EMPTY_RESPONSE_ERROR, HttpStatusCode.BadRequest);
  }

  logApiError(failedResponse?.status, e);

  switch (failedResponse.status) {
    case HttpStatusCode.Unauthorized:
      try {
        return await handleUnauthorizedResponseException<T>(e, axiosConfig);
      } catch (err) {
        Log.warn("Error trying to auto-refresh token on api call", err);

        throw err;
      }

    case HttpStatusCode.Forbidden:
      return handleForbiddenResponseException(failedResponse);

    case HttpStatusCode.NotFound:
      throw new ApiException(
        normalizeApiError({
          apiError: failedResponse.data,
          fallbackTranslationKey: errors.common.notFoundResponseStatus,
        }),
        HttpStatusCode.NotFound,
      );

    default:
      if (failedResponse.data?.status === ResponseStatusEnum.Error) {
        throw new ApiException(failedResponse.data, e.response.status);
      }

      return undefined;
  }
};

export const isBulkTranslatedErrorsResult = (
  value: unknown,
): value is ITranslatableItem[] =>
  Array.isArray(value) && value.every(item => TRANSLATION_KEY in item);
